import { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useAuth } from "@/hooks/use-auth"
import { useOnboarding } from "@/hooks/use-onboarding"
import { OnboardingStepSkeleton } from "@/components/layout/app-shell-skeleton"
import { PageContainer } from "@/components/ui/page-container"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { cn } from "@/lib/utils"
import { CURRENT_CLIENT } from "@/constants"
import {
  onboardingCanGoBack,
  onboardingProgressSummary,
  onboardingStepIndexById,
  onboardingStepLabel,
  WELCOME_STEP_ID,
  clampOnboardingStepIndex,
  nextOnboardingStepIndex,
  onboardingHomePathForGroup,
  onboardingDismissalStorageKey,
  onboardingResumeStepIndex,
  employerAccountOnboardingSteps,
} from "@/lib/onboarding-display"
import {
  EMPLOYER_ORG_SETUP_STEP_IDS,
  employerOrgSetupPath,
} from "@/lib/employer-org-setup"
import { resolveEmployerOnboardingOrgSlug } from "@/lib/employer-onboarding-organisation-api"
import type { OnboardingStepCompleteResult } from "@/lib/onboarding-step-nav"

import { WelcomeStep } from "./steps/welcome-step"
import { CVUploadStep } from "./steps/cv-upload-step"
import { IdentityStep } from "./steps/identity-step"
import { ProfileReviewStep } from "./steps/profile-review-step"
import { BasicInfoStep } from "./steps/basic-info-step"
import { SkillsStep } from "./steps/skills-step"
import { PreferencesStep } from "./steps/preferences-step"
import { EmployerPostingPreferencesStep } from "./steps/employer-posting-preferences-step"
import { OrganisationStep } from "./steps/organisation-step"
import { EmployerOrganisationStep } from "./steps/employer-organisation-step"
import { EmployerCompanyBioStep } from "./steps/employer-company-bio-step"
import { EmployerCompanyLogoStep } from "./steps/employer-company-logo-step"
import { TeamFeedbackStep } from "./steps/team-feedback-step"

const STEP_COMPONENTS: Record<string, React.ComponentType<any>> = {
  'welcome-step': WelcomeStep,
  'cv-upload-step': CVUploadStep,
  'identity-step': IdentityStep,
  'avatar-upload-step': IdentityStep,
  'profile-review-step': ProfileReviewStep,
  'basic-info-step': BasicInfoStep,
  'skills-step': SkillsStep,
  'preferences-step': PreferencesStep,
  'employer-posting-preferences-step': EmployerPostingPreferencesStep,
  'employer-organisation-step': EmployerOrganisationStep,
  'employer-company-bio-step': EmployerCompanyBioStep,
  'employer-company-logo-step': EmployerCompanyLogoStep,
  'organisation-step': OrganisationStep,
  'organization-step': OrganisationStep,
  'team-feedback-step': TeamFeedbackStep,
}

const STEPS_WITH_OWN_NAV = new Set(Object.keys(STEP_COMPONENTS))

const ONBOARDING_PAGE_CLASS = 'flex flex-col min-h-screen bg-background'
const ONBOARDING_CONTENT_CLASS = 'mt-[60px] flex flex-1 flex-col'

interface OnboardingWizardProps {
  className?: string
  onComplete?: () => void
}

export function OnboardingWizard({ className, onComplete }: OnboardingWizardProps) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const stepParam = searchParams.get('step')
  const { status, isLoading, completeStep } = useOnboarding()
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [isCompleting, setIsCompleting] = useState(false)
  const [completeError, setCompleteError] = useState<string | null>(null)

  useEffect(() => {
    if (!status?.is_completed || isLoading) return
    onComplete?.()
    navigate(onboardingHomePathForGroup(status.group_name), { replace: true })
  }, [status?.is_completed, status?.group_name, isLoading, navigate, onComplete])

  useEffect(() => {
    if (!status?.steps?.length || status.is_completed) return

    const completedIds = status.completed_step_ids ?? []
    const completed = new Set(completedIds)

    if (status.group_name === 'employer') {
      const orgSetupStepParam =
        stepParam && EMPLOYER_ORG_SETUP_STEP_IDS.includes(stepParam as (typeof EMPLOYER_ORG_SETUP_STEP_IDS)[number])
          ? stepParam
          : undefined
      const orgSetupPending =
        completed.has('organisation_setup') &&
        EMPLOYER_ORG_SETUP_STEP_IDS.some((id) => !completed.has(id))

      if (orgSetupStepParam || orgSetupPending) {
        void resolveEmployerOnboardingOrgSlug().then((slug) => {
          if (!slug) return
          const resumeStepId =
            orgSetupStepParam ??
            EMPLOYER_ORG_SETUP_STEP_IDS.find((id) => !completed.has(id)) ??
            EMPLOYER_ORG_SETUP_STEP_IDS[0]
          navigate(employerOrgSetupPath(slug, resumeStepId), { replace: true })
        })
        return
      }
    }

    const steps = employerAccountOnboardingSteps(status.steps, status.group_name)
    const resumeIndex = onboardingResumeStepIndex(steps, completedIds)
    let targetIndex = resumeIndex

    if (stepParam) {
      const requested = onboardingStepIndexById(steps, stepParam)
      if (requested >= 0) {
        targetIndex = clampOnboardingStepIndex(steps, completedIds, requested)
      }
    }

    setCurrentStepIndex(targetIndex)

    const targetStep = steps[targetIndex]
    if (targetStep && stepParam !== targetStep.id) {
      navigate(`/onboarding?step=${encodeURIComponent(targetStep.id)}`, { replace: true })
    }
  }, [status?.completed_step_ids, status?.group_name, status?.is_completed, status?.steps, stepParam, navigate])

  const goToStepIndex = (index: number, options?: { replace?: boolean }) => {
    const steps = employerAccountOnboardingSteps(status?.steps || [], status?.group_name)
    const step = steps[index]
    if (!step) return
    setCurrentStepIndex(index)
    navigate(`/onboarding?step=${encodeURIComponent(step.id)}`, {
      replace: options?.replace ?? false,
    })
  }

  if (isLoading) {
    return (
      <PageContainer maxWidth="2xl" padding="p-4" className={cn('flex flex-col min-h-screen bg-background', className)}>
        <div className={ONBOARDING_CONTENT_CLASS}>
          <OnboardingStepSkeleton ariaLabel="Loading onboarding" />
        </div>
      </PageContainer>
    )
  }

  if (status?.is_completed) {
    return (
      <PageContainer maxWidth="2xl" padding="p-4" className={cn('flex flex-col min-h-screen bg-background', className)}>
        <div className={ONBOARDING_CONTENT_CLASS}>
          <OnboardingStepSkeleton ariaLabel="Taking you to your dashboard" />
        </div>
      </PageContainer>
    )
  }

  if (!status) {
    return null
  }

  const steps = employerAccountOnboardingSteps(status.steps || [], status.group_name)
  if (steps.length === 0) {
    return (
      <PageContainer maxWidth="2xl" padding="p-4" className={cn('flex flex-col min-h-screen bg-background', className)}>
        <div className={cn(ONBOARDING_CONTENT_CLASS, 'items-center justify-center')}>
        <div className="text-muted-foreground">No onboarding steps configured</div>
        <div className="mt-4 flex justify-center">
          <Button onClick={() => navigate('/profile')}>Go to Profile</Button>
        </div>
        </div>
      </PageContainer>
    )
  }

  const activeStep = steps[currentStepIndex]
  if (!activeStep) {
    return (
      <PageContainer maxWidth="2xl" padding="p-4" className={cn('flex flex-col min-h-screen bg-background', className)}>
        <div className={cn(ONBOARDING_CONTENT_CLASS, 'items-center justify-center')}>
        <div className="text-muted-foreground">Invalid step</div>
        <div className="mt-4 flex justify-center">
          <Button onClick={() => navigate('/profile')}>Go to Profile</Button>
        </div>
        </div>
      </PageContainer>
    )
  }

  const StepComponent = STEP_COMPONENTS[activeStep.component]
  if (!StepComponent) {
    console.error(`Step component not found: ${activeStep.component}`)
    return (
      <PageContainer maxWidth="2xl" padding="p-4" className={cn('flex flex-col min-h-screen bg-background', className)}>
        <div className={cn(ONBOARDING_CONTENT_CLASS, 'items-center justify-center')}>
        <div className="text-muted-foreground">Step component not found: {activeStep.component}</div>
        <div className="mt-4 flex justify-center">
          <Button onClick={() => navigate('/profile')}>Go to Profile</Button>
        </div>
        </div>
      </PageContainer>
    )
  }

  const handleNext = async (): Promise<OnboardingStepCompleteResult> => {
    setIsCompleting(true)
    setCompleteError(null)
    const result = await completeStep(activeStep.id)
    setIsCompleting(false)

    if (!result.success) {
      const message = result.error || 'Could not complete this step'
      setCompleteError(message)
      return { success: false, error: message }
    }

    if (status.group_name === 'employer' && activeStep.id === 'organisation_setup') {
      try {
        const slug = await resolveEmployerOnboardingOrgSlug()
        if (slug) {
          navigate(employerOrgSetupPath(slug), { replace: true })
          return { success: true }
        }
      } catch (err) {
        console.error('Failed to resolve organisation for setup wizard:', err)
      }
    }

    const nextIndex = nextOnboardingStepIndex(currentStepIndex, steps)
    if (nextIndex !== null) {
      goToStepIndex(nextIndex)
    } else if (result.data?.is_completed || result.data?.is_flow_completed) {
      if (user?.email) {
        localStorage.removeItem(onboardingDismissalStorageKey(user.email, status.group_name))
      }
      onComplete?.()
      navigate(onboardingHomePathForGroup(status.group_name), { replace: true })
    }

    return { success: true }
  }

  const handleBack = () => {
    if (currentStepIndex > 0) {
      goToStepIndex(currentStepIndex - 1)
    }
  }

  const stepLabel = onboardingStepLabel(currentStepIndex, steps)
  const canGoBack = onboardingCanGoBack(currentStepIndex, steps, status.group_name)
  const progressSummary = onboardingProgressSummary(steps, status.completed_step_ids)
  const progress = stepLabel
    ? (stepLabel.current / stepLabel.total) * 100
    : progressSummary.percent
  const clientName = CURRENT_CLIENT.name || 'Yes Human'
  const isWelcomeStep = activeStep.id === WELCOME_STEP_ID

  return (
    <PageContainer maxWidth="2xl" padding="p-4" className={cn(ONBOARDING_PAGE_CLASS, className)}>
      <div className={ONBOARDING_CONTENT_CLASS}>
      {!isWelcomeStep && stepLabel ? (
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-muted-foreground">
              Step {stepLabel.current} of {stepLabel.total}
            </span>
            <span className="text-sm text-muted-foreground">
              {Math.round(progress)}%
            </span>
          </div>
          <Progress value={progress} />
        </div>
      ) : null}

      <div className={cn('flex-1 flex flex-col justify-center', isWelcomeStep && 'items-center')}>
        <div className={cn('w-full', isWelcomeStep && 'max-w-2xl')}>
          {!isWelcomeStep ? (
            <>
              <h2 className="text-2xl font-bold mb-2">{activeStep.title.replace('{client_name}', clientName)}</h2>
              <p className="text-muted-foreground mb-6">{activeStep.description}</p>
            </>
          ) : null}

          <StepComponent
            step={activeStep}
            onNext={handleNext}
            onBack={handleBack}
            isCompleting={isCompleting}
            canGoBack={canGoBack}
          />

          {completeError ? (
            <p className="mt-4 text-sm text-destructive" role="alert">
              {completeError}
            </p>
          ) : null}
        </div>
      </div>

      {!STEPS_WITH_OWN_NAV.has(activeStep.component) ? (
        <div className="mt-6 flex justify-between">
          <div>
            {canGoBack && (
              <Button variant="outline" onClick={handleBack} disabled={isCompleting}>
                Back
              </Button>
            )}
          </div>
          <Button onClick={handleNext} disabled={isCompleting}>
            {isCompleting ? 'Saving...' : currentStepIndex === steps.length - 1 ? 'Finish' : 'Continue'}
          </Button>
        </div>
      ) : null}
      </div>
    </PageContainer>
  )
}
